import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPin, Search, Navigation, Clock, Phone, ExternalLink, X, Loader2 } from 'lucide-react'
import { Loader } from '@googlemaps/js-api-loader'

const LabFinder = () => {
  const [zipCode, setZipCode] = useState('')
  const [labs, setLabs] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [selectedLab, setSelectedLab] = useState(null)

  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  const googleRef = useRef(null)

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return (R * c).toFixed(1)
  }

  // Initialize map with US view
  useEffect(() => {
    const initMap = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places']
        })

        const google = await loader.load()
        googleRef.current = google

        if (mapRef.current && !mapInstanceRef.current) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: 39.8283, lng: -98.5795 }, // Center of US
            zoom: 4,
            styles: [
              { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] }
            ],
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
          })
          mapInstanceRef.current = map
          setMapLoaded(true)
        }
      } catch (err) {
        console.error('Error loading map:', err)
      }
    }

    initMap()

    return () => {
      markersRef.current.forEach(marker => marker?.setMap(null))
      markersRef.current = []
    }
  }, [])

  const handleSearch = async (e) => {
    e.preventDefault()
    
    if (!zipCode || zipCode.length < 5) {
      setError('Please enter a valid 5-digit ZIP code')
      return
    }

    setLoading(true)
    setError(null)
    setLabs([])
    setSearched(true)

    try {
      const google = googleRef.current
      if (!google) throw new Error('Google Maps not loaded')

      // Clear existing markers
      markersRef.current.forEach(marker => marker?.setMap(null))
      markersRef.current = []

      // Geocode ZIP code
      const geocoder = new google.maps.Geocoder()
      const geocodeResult = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: zipCode + ', USA' }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0])
          } else {
            reject(new Error('Could not find location. Please check your ZIP code.'))
          }
        })
      })

      const location = geocodeResult.geometry.location
      const map = mapInstanceRef.current

      // Pan and zoom to location
      map.panTo(location)
      map.setZoom(10)

      // Add center marker
      new google.maps.Marker({
        position: location,
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#ef4444',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        },
        title: 'Your Location'
      })

      // Search for labs
      const service = new google.maps.places.PlacesService(map)
      const labResults = []

      // Helper function to check if it's an actual Quest or Labcorp location
      const isActualQuestLocation = (name) => {
        const lowerName = name.toLowerCase()
        return lowerName.includes('quest diagnostics') && 
               !lowerName.includes('any lab') && 
               !lowerName.includes('anylab')
      }

      const isActualLabcorpLocation = (name) => {
        const lowerName = name.toLowerCase()
        return (lowerName.includes('labcorp') || lowerName.includes('laboratory corporation')) && 
               !lowerName.includes('medical laboratory') &&
               !lowerName.includes('clinical laboratory') &&
               !lowerName.includes('any lab')
      }

      // Search Quest Diagnostics
      await new Promise((resolve) => {
        service.nearbySearch({
          location,
          radius: 40000,
          keyword: 'Quest Diagnostics'
        }, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            results.forEach(place => {
              // Only add if it's an actual Quest Diagnostics location
              if (isActualQuestLocation(place.name)) {
                labResults.push({
                  id: place.place_id,
                  name: 'Quest Diagnostics', // Standardize the name
                  address: place.vicinity,
                  provider: 'Quest Diagnostics',
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                  rating: place.rating,
                  distance: calculateDistance(
                    location.lat(), location.lng(),
                    place.geometry.location.lat(), place.geometry.location.lng()
                  )
                })
              }
            })
          }
          resolve()
        })
      })

      // Search Labcorp
      await new Promise((resolve) => {
        service.nearbySearch({
          location,
          radius: 40000,
          keyword: 'Labcorp'
        }, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            results.forEach(place => {
              // Only add if it's an actual Labcorp location
              if (isActualLabcorpLocation(place.name)) {
                labResults.push({
                  id: place.place_id,
                  name: 'Labcorp', // Standardize the name
                  address: place.vicinity,
                  provider: 'Labcorp',
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                  rating: place.rating,
                  distance: calculateDistance(
                    location.lat(), location.lng(),
                    place.geometry.location.lat(), place.geometry.location.lng()
                  )
                })
              }
            })
          }
          resolve()
        })
      })

      // Sort by distance
      labResults.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance))
      const topLabs = labResults.slice(0, 8)
      setLabs(topLabs)

      // Add markers for labs
      topLabs.forEach((lab, index) => {
        const marker = new google.maps.Marker({
          position: { lat: lab.lat, lng: lab.lng },
          map: map,
          title: lab.name,
          label: {
            text: String(index + 1),
            color: 'white',
            fontSize: '11px',
            fontWeight: 'bold'
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: lab.provider === 'Quest Diagnostics' ? '#f97316' : '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }
        })

        marker.addListener('click', () => {
          setSelectedLab(lab)
          map.panTo({ lat: lab.lat, lng: lab.lng })
        })

        markersRef.current.push(marker)
      })

      if (topLabs.length === 0) {
        setError('No labs found within 25 miles. Try a different ZIP code.')
      }

    } catch (err) {
      console.error('Search error:', err)
      setError(err.message || 'Error searching for labs. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLabClick = (lab) => {
    setSelectedLab(lab)
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo({ lat: lab.lat, lng: lab.lng })
      mapInstanceRef.current.setZoom(14)
    }
  }

  const getDirectionsUrl = (lab) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(lab.address)}`
  }

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Find a Lab Near You
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Over 4,500 Quest Diagnostics and Labcorp locations nationwide
            </p>
          </motion.div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
            <div className="grid lg:grid-cols-2">
              {/* Map Section */}
              <div className="relative h-[400px] lg:h-[500px] bg-gray-100">
                <div 
                  ref={mapRef}
                  className="absolute inset-0"
                  style={{ width: '100%', height: '100%' }}
                />
                
                {!mapLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-2" />
                      <p className="text-gray-500">Loading map...</p>
                    </div>
                  </div>
                )}

                {/* Search Box Overlay on Map */}
                <div className="absolute top-4 left-4 right-4 z-10">
                  <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-lg p-2">
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                          placeholder="Enter ZIP Code"
                          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                          maxLength={5}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Search className="w-5 h-5" />
                        )}
                        <span className="hidden sm:inline">Find</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Legend */}
                <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-3 z-10">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                      <span>Quest</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                      <span>Labcorp</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {searched ? `${labs.length} Labs Found` : 'Nearby Locations'}
                  </h3>
                  {labs.length > 0 && (
                    <span className="text-sm text-gray-500">Within 25 miles</span>
                  )}
                </div>

                {/* Important Disclaimer */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {!searched && !loading && (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="w-10 h-10 text-primary-600" />
                    </div>
                    <p className="text-gray-600 mb-2">Enter your ZIP code to find</p>
                    <p className="text-gray-600">Quest Diagnostics and Labcorp locations near you</p>
                  </div>
                )}

                {loading && (
                  <div className="text-center py-12">
                    <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-gray-600">Searching for labs near you...</p>
                  </div>
                )}

                {searched && !loading && labs.length > 0 && (
                  <div className="space-y-3 max-h-[380px] overflow-y-auto pr-2">
                    {labs.map((lab, index) => (
                      <motion.button
                        key={lab.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleLabClick(lab)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          selectedLab?.id === lab.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-100 hover:border-primary-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${
                            lab.provider === 'Quest Diagnostics' ? 'bg-orange-500' : 'bg-blue-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-semibold text-gray-900 truncate">{lab.name}</h4>
                              <span className="text-sm text-gray-500 whitespace-nowrap">{lab.distance} mi</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 truncate">{lab.address}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                                lab.provider === 'Quest Diagnostics' 
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {lab.provider}
                              </span>
                              <a
                                href={getDirectionsUrl(lab)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                              >
                                <Navigation className="w-3 h-3" />
                                Directions
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Selected Lab Details */}
                <AnimatePresence>
                  {selectedLab && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="mt-4 p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-100"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{selectedLab.name}</p>
                          <p className="text-sm text-gray-600 mt-1">{selectedLab.address}</p>
                        </div>
                        <button
                          onClick={() => setSelectedLab(null)}
                          className="p-1 hover:bg-white rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <a
                          href={getDirectionsUrl(selectedLab)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Navigation className="w-4 h-4" />
                          Get Directions
                        </a>
                        <a
                          href="/tests"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                        >
                          Order Tests
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '4,500+', label: 'Lab Locations' },
              { value: '50', label: 'States Covered' },
              { value: '1-3', label: 'Days for Results' },
              { value: '85%', label: 'Avg. Savings' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-4"
              >
                <p className="text-3xl font-bold text-primary-600">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default LabFinder
